import { useState } from 'react';
import { Link } from 'wouter';
import { t } from 'i18next';

import appConfig from '../../../../../app.config';
import { useAppDispatch, useAppSelector } from '../../../../state/hooks';
import { selectPlayers } from '../../../../state/selectors';
import { show as showTour } from '../../../../slices/local/tour';
import SentryFeedbackButton from '../../../partials/SentryFeedbackButton';

import logo from '../styles/assets/images/downloadplayer-logo.png';
import { Copyright, PartnerLogo } from './Partner';
import { CampaignIcon, DashboardIcon, ExpandIcon, GraphicEqIcon, HelpIcon, MusicNoteIcon, QueueMusicIcon } from './Icons';

const itemClassName = 'flex items-center gap-4 rounded-full px-4 h-12 text-base transition-colors';

const NavItem = ({ href, icon: Icon, active, className = '', onNavigate, children }) => (
  <Link
    href={href}
    onClick={onNavigate}
    className={`${itemClassName} ${active ? 'bg-ms-accent-container text-ms-on-accent-container font-medium' : 'text-ms-text hover:bg-ms-raised'} ${className}`}
  >
    <Icon className="w-6 h-6 shrink-0" />
    <span className="truncate">{children}</span>
  </Link>
);

const ZoneGroup = ({ player, channel, route, onNavigate }) => {
  const isCurrent = route.channel == channel.channel;
  const [open, setOpen] = useState(isCurrent);
  const base = `/${player.id}/${channel.channel}`;

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className={`w-full ${itemClassName} cursor-pointer text-ms-text hover:bg-ms-raised app-player-channel-${channel.channel} ${isCurrent ? 'font-medium' : ''}`}
      >
        <MusicNoteIcon className="w-6 h-6 shrink-0" />
        <span className="flex-1 truncate text-left">{channel.channel} - {channel.name || player.name}</span>
        <ExpandIcon className={`w-6 h-6 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="mt-1 ml-10 flex flex-col gap-1">
          <NavItem href={base} icon={QueueMusicIcon} active={isCurrent && !route.stationId} onNavigate={onNavigate}>
            {t('musicstyling.nav.stations')}
          </NavItem>
          {channel.stationId && (
            <NavItem href={`${base}/${channel.stationId}`} icon={GraphicEqIcon} active={isCurrent && route.stationId == channel.stationId} onNavigate={onNavigate}>
              {t('musicstyling.nav.now_playing')}
            </NavItem>
          )}
        </div>
      )}
    </div>
  );
};

export const Sidebar = ({ route, onNavigate }) => {
  const dispatch = useAppDispatch();
  const players = useAppSelector(selectPlayers);
  const playerList = Object.values(players);
  const player = route.playerId ? players[route.playerId] : (playerList.length === 1 ? playerList[0] : null);
  const channels = player ? Object.values(player.channels).sort((a, b) => a.channel - b.channel) : [];

  return (
    <div className="flex flex-col h-full bg-ms-sidebar rounded-br-2xl">
      <div className="h-16 shrink-0 flex items-center px-5">
        <Link href="/" onClick={onNavigate}>
          <img src={logo} alt="Download Player" className="w-[150px] h-auto" />
        </Link>
      </div>

      <nav className="flex-1 overflow-y-auto ms-scroll px-3 py-4 flex flex-col gap-1">
        {playerList.length > 1 && (
          <NavItem href="/" icon={DashboardIcon} active={!route.playerId} onNavigate={onNavigate} className="app-navigate-playeroverview">
            {t('musicstyling.nav.overview')}
          </NavItem>
        )}

        {player && (
          <>
            <div className="mt-4 mb-1 px-4 text-xs uppercase tracking-wider text-ms-muted truncate" title={player.name}>
              {player.name}
            </div>
            {channels.map(channel => (
              <ZoneGroup
                key={`${player.id}-${channel.channel}`}
                player={player}
                channel={channel}
                route={{ ...route, channel: route.channel ?? Math.min(...channels.map(c => c.channel)) }}
                onNavigate={onNavigate}
              />
            ))}
          </>
        )}
      </nav>

      <div className="shrink-0 px-3 pt-2 pb-5 flex flex-col gap-1">
        {appConfig.showOnboarding && (
          <button
            type="button"
            onClick={() => dispatch(showTour())}
            className={`${itemClassName} text-ms-text hover:bg-ms-raised cursor-pointer`}
          >
            <HelpIcon className="w-6 h-6" />
            {t('footer.tour_guide')}
          </button>
        )}
        {appConfig.showFeedback && (
          <div className="app-report-issue-button">
            <SentryFeedbackButton className={`w-full ${itemClassName} text-ms-text hover:bg-ms-raised cursor-pointer`}>
              <CampaignIcon className="w-6 h-6" />
              {t('feedback.triggerLabel')}
            </SentryFeedbackButton>
          </div>
        )}
        <div className="px-4 pt-4">
          <PartnerLogo />
          <div className="text-sm text-ms-text mt-2">
            <Copyright />{' '}
            <span className="text-ms-muted">{process.env.REACT_APP_VERSION} ({process.env.REACT_APP_COMMIT_HASH})</span>
          </div>
        </div>
      </div>
    </div>
  );
};
